import Cookies from 'js-cookie';

export interface CookieStorageOptions {
  /**
   * Cookie expiration in days
   * @default 1
   */
  expires?: number;
  /**
   * Cookie path
   * @default '/'
   */
  path?: string;
  /**
   * Cookie sameSite attribute
   * @default 'strict'
   */
  sameSite?: 'strict' | 'lax' | 'none';
}

/**
 * Default cookie storage options
 */
const defaultOptions: CookieStorageOptions = {
  path: '/',
  sameSite: 'strict',
  expires: 1, // 1 day
};

/**
 * Creates a storage interface for a simple value type
 * @param typeGuard Function to validate the value type
 * @param defaultValue Default value to use if cookie is invalid or missing
 * @param options Cookie storage options
 */
export function createCookieValueStorage<T>(
  typeGuard: (value: unknown) => value is T,
  defaultValue: T,
  options: CookieStorageOptions = {}
) {
  const cookieOptions = { ...defaultOptions, ...options };

  return {
    getItem: (key: string): T => {
      const value = Cookies.get(key);

      return value && typeGuard(value) ? value : defaultValue;
    },
    setItem: (key: string, newValue: T): void => {
      if (typeGuard(newValue)) {
        Cookies.set(key, String(newValue), cookieOptions);
      }
    },
    removeItem: (key: string): void => {
      Cookies.remove(key, { path: cookieOptions.path });
    },
  };
}

/**
 * Creates a storage interface for a Set of values
 * @param typeGuard Function to validate each item in the set
 * @param options Cookie storage options
 */
export function createCookieSetStorage<T extends string>(
  typeGuard: (value: unknown) => value is T,
  options: CookieStorageOptions = {}
) {
  const cookieOptions = { ...defaultOptions, ...options };

  return {
    getItem: (key: string): Set<T> => {
      const value = Cookies.get(key);

      if (!value) {
        return new Set<T>();
      }

      // Convert from comma-separated string to typed Set
      const items = value.split(',').filter(typeGuard);

      return new Set<T>(items);
    },
    setItem: (key: string, newValue: Set<T>): void => {
      // Convert typed Set to comma-separated string
      // Filter out any potentially invalid values
      const stringValue = Array.from(newValue).filter(typeGuard).join(',');

      Cookies.set(key, stringValue, cookieOptions);
    },
    removeItem: (key: string): void => {
      Cookies.remove(key, { path: cookieOptions.path });
    },
  };
}
